/**
 * gc-cart-drawer.js — stable line identification for girlcave cart quantity changes.
 *
 * Root cause (measured 2026-08-30 on girlcave.shop): this store's cart re-sorts
 * its line items between mutations. Adding S to a cart of [XS] lands the cart
 * as [S, XS] — sorted, not appended — and the sort can apply after the add
 * response has already been rendered into the drawer. Dawn's cart drawer
 * identifies the line to change positionally (data-index → { line: n } in
 * /cart/change.js), so a drawer painted before a re-sort sends a line number
 * that no longer points at the clicked row: pressing + on S then changed XS,
 * the row that had slid into the clicked position.
 *
 * Fix: the girlcave drawer markup (snippets/gc-cart-drawer.liquid) renders
 * data-quantity-line-key="{{ item.key }}" on every quantity input. This file
 * swaps the drawer's cart-drawer-items updateQuantity for a key-based one —
 * { id: key } in the /cart/change.js body, which the Cart AJAX API documents
 * as the stable identifier ("it's recommended to use the line item key to
 * ensure that you're only changing the intended line item"). The key is
 * resolved from the interaction itself — change events target the input,
 * remove clicks carry the row's button as currentTarget — so it is correct
 * no matter how the server ordered the cart. data-index stays in the markup
 * for Dawn's DOM ids (loading, error slots, focus fallback).
 *
 * Scope: prototype patch on cart.js's CartItems base class, so it serves both
 * girlcave cart surfaces — the drawer (cart-drawer-items inherits it) and,
 * since 2026-09-24, the /cart page (sections/gc-main-cart.liquid renders the
 * same keyed rows inside Dawn's own cart-items element). Loaded from
 * layout/theme.liquid AFTER cart-drawer.js (both deferred, so document order
 * applies) — patching the prototype covers instances created earlier. With a
 * non-drawer cart type the cart page section loads this file itself, after
 * its cart.js.
 *
 * The method below is Dawn's cart.js updateQuantity with the same structure
 * (performance markers, loading toggles, section splicing, live regions,
 * error paths) and exactly three changes, marked CHANGED:
 *   1. request body: { id: lineKey, quantity } instead of { line, quantity }
 *      (markup without line keys keeps Dawn's { line } — never { id: line },
 *      which the Cart API would read as a variant id)
 *   2. updatedValue read from parsedState.items by key instead of items[line - 1]
 *   3. post-re-render row resolution (clamp message + refocus) by key — the
 *      re-rendered drawer uses the server's line order, so a stale positional
 *      id can point at a different row
 *
 * Plus one page-only override, onCartUpdate, at the end of the file.
 */
(function () {
  if (typeof CartItems === 'undefined') return;

  function resolveLineKey(line, event) {
    const fromEvent =
      event?.target?.dataset?.quantityLineKey ||
      event?.currentTarget?.closest?.('.cart-item')?.querySelector('input[name="updates[]"]')?.dataset
        .quantityLineKey;
    if (fromEvent) return fromEvent;
    const input = document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
    return input?.dataset.quantityLineKey;
  }

  CartItems.prototype.updateQuantity = function (line, quantity, event, name, variantId) {
    const eventTarget = event?.currentTarget instanceof CartRemoveButton ? 'clear' : 'change';
    const cartPerformanceUpdateMarker = CartPerformance.createStartingMarker(`${eventTarget}:user-action`);

    this.enableLoading(line);

    const action = quantity === 0 ? 'remove' : 'update';
    // Painted-DOM positional lookups stay correct for pre-render work: the
    // data-index was painted on this row, so ids inside the current DOM
    // resolve to the clicked row regardless of the server's order.
    const quantityInput = this.querySelector(`#Quantity-${line}`) || this.querySelector(`#Drawer-quantity-${line}`);
    const lineVariantId = variantId || quantityInput?.dataset.quantityVariantId;
    const lineKey = resolveLineKey(line, event) || quantityInput?.dataset.quantityLineKey;
    const linesUpdateDeferred = this.createCartLinesUpdateEvent(action, lineVariantId, quantity, lineKey);

    // Cache sections before the fetch so we read dataset.id while elements still exist in the DOM
    const sectionsToRender = this.getSectionsToRender();

    // CHANGED (1) — the line key, not the position, identifies the line.
    const body = JSON.stringify({
      ...(lineKey ? { id: lineKey } : { line }),
      quantity,
      sections: sectionsToRender.map((section) => section.section),
      sections_url: window.location.pathname,
    });

    fetch(`${routes.cart_change_url}`, { ...fetchConfig(), ...{ body } })
      .then((response) => {
        return response.text();
      })
      .then((state) => {
        const parsedState = JSON.parse(state);

        if (parsedState.errors) {
          this.dispatchCartErrorEvent(parsedState.errors, 'INVALID');
          linesUpdateDeferred?.reject(new Error(parsedState.errors));
        } else {
          this.resolveCartLinesUpdate(linesUpdateDeferred, parsedState);
        }

        CartPerformance.measure(`${eventTarget}:paint-updated-sections`, () => {
          const quantityElement =
            document.getElementById(`Quantity-${line}`) || document.getElementById(`Drawer-quantity-${line}`);
          const items = document.querySelectorAll('.cart-item');

          if (parsedState.errors) {
            quantityElement.value = quantityElement.getAttribute('value');
            this.updateLiveRegions(line, parsedState.errors);
            return;
          }

          this.classList.toggle('is-empty', parsedState.item_count === 0);
          const cartDrawerWrapper = document.querySelector('cart-drawer');
          const cartFooter = document.getElementById('main-cart-footer');

          if (cartFooter) cartFooter.classList.toggle('is-empty', parsedState.item_count === 0);
          if (cartDrawerWrapper) cartDrawerWrapper.classList.toggle('is-empty', parsedState.item_count === 0);

          sectionsToRender.forEach((section) => {
            const elementToReplace =
              document.getElementById(section.id).querySelector(section.selector) ||
              document.getElementById(section.id);
            elementToReplace.innerHTML = this.getSectionInnerHTML(
              parsedState.sections[section.section],
              section.selector
            );
          });

          // CHANGED (2) + (3) — everything below runs against the
          // re-rendered drawer, whose line order is the server's; resolve
          // the changed row by key, never by the stale position.
          const lineItem = lineKey
            ? this.querySelector(`input[data-quantity-line-key="${CSS.escape(lineKey)}"]`)?.closest('.cart-item')
            : document.getElementById(`CartItem-${line}`) || document.getElementById(`CartDrawer-Item-${line}`);
          const updatedValue = lineKey
            ? parsedState.items.find((item) => item.key === lineKey)?.quantity
            : parsedState.items[line - 1]?.quantity;
          let message = '';
          if (items.length === parsedState.items.length && updatedValue !== parseInt(quantity)) {
            if (typeof updatedValue === 'undefined') {
              message = window.cartStrings.error;
            } else {
              message = window.cartStrings.quantityError.replace('[quantity]', updatedValue);
            }
          }
          const lineItemError = lineItem?.querySelector('.cart-item__error-text');
          if (lineItemError) lineItemError.textContent = message;

          this.lineItemStatusElement.setAttribute('aria-hidden', true);

          const cartStatus =
            document.getElementById('cart-live-region-text') || document.getElementById('CartDrawer-LiveRegionText');
          cartStatus.setAttribute('aria-hidden', false);

          setTimeout(() => {
            cartStatus.setAttribute('aria-hidden', true);
          }, 1000);

          if (lineItem && lineItem.querySelector(`[name="${name}"]`)) {
            cartDrawerWrapper
              ? trapFocus(cartDrawerWrapper, lineItem.querySelector(`[name="${name}"]`))
              : lineItem.querySelector(`[name="${name}"]`).focus();
          } else if (parsedState.item_count === 0 && cartDrawerWrapper?.querySelector('.drawer__inner-empty')) {
            trapFocus(cartDrawerWrapper.querySelector('.drawer__inner-empty'), cartDrawerWrapper.querySelector('a'));
          } else if (document.querySelector('.cart-item') && cartDrawerWrapper) {
            trapFocus(cartDrawerWrapper, document.querySelector('.cart-item__name'));
          }
        });

        publish(PUB_SUB_EVENTS.cartUpdate, { source: 'cart-items', cartData: parsedState, variantId: variantId });
      })
      .catch((e) => {
        this.querySelectorAll('.loading__spinner').forEach((overlay) => overlay.classList.add('hidden'));
        const errors = document.getElementById('cart-errors') || document.getElementById('CartDrawer-CartErrors');
        if (errors) errors.textContent = window.cartStrings.error;
        this.dispatchCartErrorEvent(window.cartStrings.error, 'SERVICE_UNAVAILABLE');
        linesUpdateDeferred?.reject(e);
      })
      .finally(() => {
        this.disableLoading(line);
        CartPerformance.measureFromMarker(`${eventTarget}:user-action`, cartPerformanceUpdateMarker);
      });
  };

  // Page-only. Dawn's onCartUpdate for the page re-fetches
  // ?section_id=main-cart-items — Dawn's section FILE, rendered statically —
  // and pastes its table into the element. It fires on every cartUpdate the
  // page did not publish itself, notably the Standard Actions refresh
  // (standard-actions-override.js leaves 'cart-items:main-cart-items' to this
  // subscriber). The girlcave page (cart-items[data-gc-cart-page]) re-renders
  // from its own section and the summary section instead, splicing only the
  // two .js-contents slots so the live regions CartItems holds references to
  // survive. Every other cart-items element keeps Dawn's method.
  const dawnOnCartUpdate = CartItems.prototype.onCartUpdate;

  CartItems.prototype.onCartUpdate = function () {
    const mount = this.hasAttribute('data-gc-cart-page') ? this.querySelector('#main-cart-items') : null;
    if (!mount) return dawnOnCartUpdate.call(this);

    const footer = document.getElementById('main-cart-footer');
    const ids = [mount.dataset.id, footer?.dataset.id].filter(Boolean);
    const parse = (html) => new DOMParser().parseFromString(html, 'text/html');

    return fetch(`${routes.cart_url}?sections=${ids.join(',')}`)
      .then((response) => response.json())
      .then((sections) => {
        if (!sections[mount.dataset.id]) return;
        const page = parse(sections[mount.dataset.id]);
        const items = page.querySelector('#main-cart-items .js-contents');
        if (items) mount.querySelector('.js-contents').innerHTML = items.innerHTML;

        const isEmpty = !!page.querySelector('cart-items.is-empty');
        this.classList.toggle('is-empty', isEmpty);
        if (!footer) return;
        footer.classList.toggle('is-empty', isEmpty);
        const summary = sections[footer.dataset.id] && parse(sections[footer.dataset.id]).querySelector('.js-contents');
        if (summary) footer.querySelector('.js-contents').innerHTML = summary.innerHTML;
      })
      .catch((e) => {
        console.error(e);
      });
  };
})();
